// useLogoBrightness.js — client-side classifier for whether a logo is
// predominantly DARK against a dark HQ card background. Kept SIMPLE:
// one hook, one shared cache, one shared in-flight promise per URL.
//
// Return values:
//   • "dark"    → caller renders the subtle halo
//   • "light"   → no halo
//   • "unknown" → treat identically to "light" (never over-apply)
//
// Every failure path (CORS taint, decode error, empty SVG, timeout)
// resolves to "unknown" so nothing ever crashes or spams the console.

import { useEffect, useState } from "react";

const CACHE = new Map();       // url -> "dark" | "light" | "unknown"
const IN_FLIGHT = new Map();   // url -> Promise<"dark" | "light" | "unknown">

const DARK_LUMA_MAX = 96;      // <96 avg luma on visible pixels = "dark"
const ALPHA_MIN = 32;          // skip near-transparent pixels
const SAMPLE_MAX = 32;         // downsample to at most 32×32

function classify(img) {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) return "unknown";
  const scale = Math.min(1, SAMPLE_MAX / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, cw, ch);
  let data;
  try {
    data = ctx.getImageData(0, 0, cw, ch).data;
  } catch {
    // CORS taint — canvas is unreadable. Fail safely.
    return "unknown";
  }
  let sum = 0;
  let visible = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < ALPHA_MIN) continue;
    sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    visible++;
  }
  if (!visible) return "unknown";
  return sum / visible < DARK_LUMA_MAX ? "dark" : "light";
}

function loadAndClassify(url) {
  return new Promise((resolve) => {
    let done = false;
    const finish = (result) => {
      if (done) return;
      done = true;
      CACHE.set(url, result);
      IN_FLIGHT.delete(url);
      resolve(result);
    };
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => {
      try { finish(classify(img)); }
      catch { finish("unknown"); }
    };
    img.onerror = () => finish("unknown");
    setTimeout(() => finish("unknown"), 4000);
    img.src = url;
  });
}

export function useLogoBrightness(src) {
  const [classification, setClassification] = useState(() =>
    src ? (CACHE.get(src) || "unknown") : "unknown"
  );

  useEffect(() => {
    if (!src) {
      setClassification("unknown");
      return;
    }
    const cached = CACHE.get(src);
    if (cached) {
      setClassification(cached);
      return;
    }
    let alive = true;
    let promise = IN_FLIGHT.get(src);
    if (!promise) {
      promise = loadAndClassify(src);
      IN_FLIGHT.set(src, promise);
    }
    promise.then((result) => {
      if (alive) setClassification(result);
    });
    return () => { alive = false; };
  }, [src]);

  return { classification };
}

export function _resetLogoBrightnessCache() {
  CACHE.clear();
  IN_FLIGHT.clear();
}
